import React, { Suspense, useEffect, useState } from 'react';
import { List } from "immutable";
import { Provider, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from  'react-router';
import { createStore, applyMiddleware } from 'redux';
import PrivateRoute from "./shared/auth/PrivateRoute";
import { Route, Routes, useLocation} from "react-router-dom";
import ReduxThunk from 'redux-thunk'
import { useAuth0 } from "@auth0/auth0-react";
import jwt_decode from "jwt-decode";
import * as Sentry from '@sentry/react';
import { setUserData, setOrganization, showSnackbar } from "./shared/stores/app_s";

import rootReducer from './shared/stores/root_s';
import _API from './shared/API';
import  OrganizationDateTime from './shared/dates';
import { activityLogMiddleware } from './pages/jobs/job_dashboard/store/activityLogMiddleware';

import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { theme } from './shared/styles/appStyles';
import LoginPage from './shared/auth/LoginPage';
import { catchBlockErrors } from './shared/utils/sentry';
import App from './App';
import Loading from "./shared/components/SpinnerLoading";
import Page404 from "./shared/components/Page404";

const store = createStore(rootReducer, applyMiddleware(ReduxThunk, activityLogMiddleware));

// export the api class after we pass it dispatch
export const API = new _API(store.dispatch);
export const OrgDateTime = new OrganizationDateTime();

function LoadingRoute(props){
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isAppLoading, setAppLoading] = useState(true);
    const { isAuthenticated, user, getAccessTokenSilently, logout } = useAuth0();
    const pages = useSelector((state) =>
        state.app.getIn(["user", "pages"], List([]))
    );
    const closeAppLoading = () => setAppLoading(false);

    useEffect(() => {
        if (user && isAuthenticated) {
            (async () => {
                try {
                    const userId = user.sub.split("|")[1];
                    const token = await getAccessTokenSilently();
                    API.setToken(token);
                    // Lets long-lived consumers (the report status websocket) renew
                    // the token after this one expires, instead of dying with it.
                    API.setTokenProvider(getAccessTokenSilently);
                    const permissions = jwt_decode(token).permissions;

                    dispatch({
                        type: 'setPermissions',
                        permissions: permissions
                    });

                    const userResponse = await API.call({
                        endpoint: API.endpoints['SL/userInfo'],
                        method: 'get'
                    });
                    
                    if (!userResponse || userResponse.error) {
                        console.error('User not found on backend, logging out');
                        
                        dispatch(showSnackbar({
                            message: 'Your account could not be found. Please contact support.',
                            variant: 'error',
                            persist: true
                        }));
                        
                        setTimeout(() => {
                            logout({ 
                                logoutParams: { returnTo: window.location.origin } 
                            });
                        }, 2000);
                        return;
                    }
                    
                    dispatch(setUserData(userResponse));
                    
                    const id = userResponse.get('_id');
                    const username = userResponse.get('username');
                    const email = userResponse.get('email');
                    const _userObj = { id, username, email };
                    
                    Sentry.configureScope((scope) => {
                        scope.setUser(_userObj);
                    });
                    Sentry.configureScope((scope) => {
                        scope.setTag('User', username);
                    });
                    
                    const orgResponse = await API.call({
                        endpoint: API.endpoints['SL/orgInfo'],
                        method: 'get'
                    });
                    
                    if (!orgResponse || orgResponse.error) {
                        console.error('Org not found on backend, logging out');
                        
                        dispatch(showSnackbar({
                            message: 'Your organization could not be found. Please contact support.',
                            variant: 'error',
                            persist: true
                        }));
                        
                        setTimeout(() => {
                            logout({ 
                                logoutParams: { returnTo: window.location.origin } 
                            });
                        }, 2000);
                        return;
                    }
                    
                    dispatch(setOrganization(orgResponse));
                    
                } catch (error) {
                    catchBlockErrors({
                        error,
                        "file/function": "Root.js LoadingRoute user fetch"
                    });
                    
                    dispatch(showSnackbar({
                        message: 'Failed to load your account. You will be logged out.',
                        variant: 'error',
                        persist: true
                    }));
                    
                    setTimeout(() => {
                        logout({ 
                            logoutParams: { returnTo: window.location.origin } 
                        });
                    }, 2000);
                }
            })();
        }
    }, [isAuthenticated, user, logout, dispatch]);

    useEffect(() => {
        if (pages.size === 0) return;
        const defaultPage = pages.getIn([0, 'link'], '');
        if (defaultPage) {
            const finalLink = location === "/" ? defaultPage : location;
            closeAppLoading();
            navigate(finalLink);
        }

    }, [pages]);

    if (isAppLoading ) {
        return <Loading />
    }
    return (
        <App {...props} />
    )
};

function Root(props) {
    const { isLoading, isAuthenticated, logout } = useAuth0();

    const _logout = (e) => {
        // e.preventDefault();
        return logout({ 
            clientId: '',
            logoutParams: {
                returnTo: process.env.REACT_APP_AUTH0_LOGOUT_URL 
            }
        });
    }
    // _logout();
    try {
        if (isLoading) {
            return (
                <div>
                    <Loading />
                </div>
            );
        } else if (!isAuthenticated) {
            return <LoginPage />;
        }

    } catch (error) {
        catchBlockErrors({ error, fileFunction: "Root/second one" });
        return <LoginPage />;
    }

    // only return the app once we are authenticated
    return isAuthenticated && (
        <Provider store={store}>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme}>
                    <Routes>
                        <Route
                            path="/*"
                            element={
                                <LoadingRoute 
                                    {...props} 
                                />
                            }
                        />
                    </Routes>
                </ThemeProvider>
            </StyledEngineProvider>
        </Provider>
    );
}


export default Root;
